import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { EIcon, SvgIcon } from '@common/general-ui/svg-icon/svg-icon.component';
import { ISerializedStylesList } from '@common/interfaces/serialized-styles-list.interface';
import { Modal as AntModal } from 'antd';
import React, { ReactNode } from 'react';
import { css } from 'twin.macro';

export type TModalVariant = 'primary' | 'secondary' | 'tertiary';
interface IProps {
    variant?: TModalVariant;
    name: string;
    className?: string;
    children: ReactNode;
    title: ReactNode;
    renderFooter?: () => ReactNode;
    onCloseClick?: () => void;
    closable?: boolean;
    centered?: boolean;
}

export type IModalReturn = [() => void, () => void];

export const Modal = ({
    name,
    className,
    children,
    title,
    variant = 'primary',
    onCloseClick,
    renderFooter,
    closable = true,
    centered = false,
}: IProps): JSX.Element => {
    return (
        <AntModal
            centered={centered}
            modalRender={(node): JSX.Element => <div data-testid={`modal-${name}`}>{node}</div>}
            width={''}
            className={className}
            onCancel={onCloseClick}
            css={styles.root(variant, !!renderFooter)}
            title={title}
            visible={true}
            closable={closable}
            footer={renderFooter ? <>{renderFooter()}</> : null}
            closeIcon={
                variant === 'tertiary' ? (
                    <span className="anticon-close">
                        <SvgIcon icon={EIcon.close} />
                    </span>
                ) : undefined
            }
        >
            {children}
        </AntModal>
    );
};

const styles = {
    root: (variant: TModalVariant, hasFooter: boolean): ISerializedStylesList => [
        css`
            top: 0;

            .ant-modal-content {
                background-color: transparent;
                margin: 8px;
                margin-top: 2px;
                border-radius: 6px;
                ${MQ.lg} {
                    margin-top: 40px;
                }
            }
            .ant-modal-header {
                border-radius: 6px 6px 0px 0px;
                color: white;
				border:0px;
            }
            .ant-modal-body {
                background-color: white;
                padding: 24px;
				border-radius: 0px 0px 6px 6px;
            }
            .ant-modal-footer {
                background-color: white;
                padding: 16px;
                border-radius: 0px 0px 6px 6px;
                ${MQ.md} {
                  padding: 16px 24px;
              }
            }
            .ant-modal-title {
                color: white;
                font-size: ${THEME.BASE_FONT_SIZE};
                font-weight: 500;
            }
            .ant-modal-close-x {
                margin-top: -2px;
                color: white;

                svg {
                    width: 16px;
                    height: 16px;
                }
            && .ant-modal-footer {
                .ant-btn {
                    margin: 0px;
            }
            .ant-modal-footer .ant-btn + .ant-btn:not(.ant-dropdown-trigger) {
                margin-left: unset;
            }
        `,
        hasFooter &&
            `.ant-modal-body {
				 border-radius: 0;
            }`,
        variant === 'primary' &&
            css`
                .ant-modal-header {
                    background-color: ${THEME.GREEN};
                    color: white;
                }
            `,
        variant === 'secondary' &&
            css`
                .ant-modal-header {
                    background-color: ${THEME.LIGHT_GREEN};
                    color: white;
                }
            `,
        variant === 'tertiary' &&
            css`
                .ant-modal-header {
                    color: black;
                    border-bottom: 1px solid ${THEME.GRAY_200};
                }
                .ant-modal-title {
                    color: black;
                }

                .ant-modal-close-x {
                    color: black;
                    display: flex;
                    margin-top: -1px;
                    justify-content: flex-end;

                    padding-right: 16px;
                    align-items: center;

                    .anticon-close {
                        display: block;
                        width: 24px;
                        height: 24px;
                        padding: 7px;
                        svg {
                            display: block;
                            width: 10px;
                            height: 10px;
                            color: black;
                            font-weight: bold;
                        }
                        :hover {
                            background-color: ${THEME.GRAY_100};
                            border-radius: 50%;
                        }
                    }
                }
            `,
    ],
};
